import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Book, BookOpen, LayoutGrid, Loader2, Newspaper, Podcast, Settings2 } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { MobileNav } from "@/components/MobileNav";
import { SearchBar } from "@/components/SearchBar";
import { SeriesCard } from "@/components/SeriesCard";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import { useSeriesList } from "@/hooks/useSeries";
import { usePlayableVideoCountBySeries } from "@/hooks/useVideos";
import { useMembershipStatus } from "@/hooks/useMembershipStatus";
import { useTrialSeries } from "@/hooks/useTrialSeries";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { cn, safeDate } from "@/lib/utils";
import { getLiveDemoSeriesWeight, isLiveDemoBlockedSeriesTitle } from "@/lib/live-demo";
import {
  DEFAULT_LIVE_DEMO_CONFIG,
  formatLiveConfigListInput,
  parseLiveConfigListInput,
  readLiveDemoConfig,
  saveLiveDemoConfig,
  sanitizeLiveDemoConfig,
  type LiveDemoConfig,
} from "@/lib/live-demo-config";
import { categoryBelongsToTab, normalizeCategoryList, type PrimaryCategoryTab } from "@/lib/category-config";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 12;

function extractSeriesCategories(series: any): string[] {
  return normalizeCategoryList([
    series?.category,
    ...(Array.isArray(series?.categories) ? series.categories : []),
  ]);
}

function seriesMatchesTab(series: any, tab: PrimaryCategoryTab): boolean {
  if (tab === "all") return true;
  const categories = extractSeriesCategories(series);
  return categories.some((value) => categoryBelongsToTab(value, tab));
}

function pickWeightedSeries<T extends { title?: unknown }>(items: T[]): T | undefined {
  if (!Array.isArray(items) || items.length === 0) return undefined;

  const weighted = items
    .map((item) => ({ item, weight: Math.max(0, getLiveDemoSeriesWeight(item?.title)) }))
    .filter((entry) => entry.weight > 0);

  if (weighted.length === 0) {
    return items[Math.floor(Math.random() * items.length)];
  }

  const total = weighted.reduce((sum, entry) => sum + entry.weight, 0);
  let cursor = Math.random() * total;
  for (const entry of weighted) {
    cursor -= entry.weight;
    if (cursor <= 0) return entry.item;
  }
  return weighted[weighted.length - 1]?.item;
}

export default function Index() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState<PrimaryCategoryTab>("all");
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<"updateTime" | "favoriteCount">("updateTime");
  const [listPage, setListPage] = useState(1);
  const [liveConfig, setLiveConfig] = useState<LiveDemoConfig>(() => readLiveDemoConfig());

  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const updateLiveConfig = useCallback((patch: Partial<LiveDemoConfig>) => {
    setLiveConfig((prev) => {
      const next = sanitizeLiveDemoConfig({ ...prev, ...patch });
      saveLiveDemoConfig(next);
      return next;
    });
  }, []);

  const resetLiveConfig = useCallback(() => {
    setLiveConfig(DEFAULT_LIVE_DEMO_CONFIG);
    saveLiveDemoConfig(DEFAULT_LIVE_DEMO_CONFIG);
  }, []);

  const updateLiveListConfig = useCallback(
    (key: "blockedVideoIds" | "blockedVideoKeywords", raw: string) => {
      updateLiveConfig({ [key]: parseLiveConfigListInput(raw) } as Partial<LiveDemoConfig>);
    },
    [updateLiveConfig],
  );

  const { data: seriesList = [], isLoading: isSeriesLoading } = useSeriesList();
  const { data: membership, isLoading: isMembershipLoading } = useMembershipStatus(user?.id);
  const { data: trial, isLoading: isTrialLoading } = useTrialSeries();

  const isLoading = isSeriesLoading || isMembershipLoading || isTrialLoading;
  const isLiveDemoMode = useMemo(() => {
    if (!membership?.isAdmin) return false;
    const params = new URLSearchParams(location.search);
    return params.get("live") === "1";
  }, [location.search, membership?.isAdmin]);
  const liveExcludeSeriesId = useMemo(() => {
    if (!isLiveDemoMode) return "";
    const params = new URLSearchParams(location.search);
    return params.get("excludeSeries") || "";
  }, [isLiveDemoMode, location.search]);

  const visibleSeriesList = useMemo(() => {
    const byHidden = membership?.isAdmin ? seriesList : seriesList.filter((item: any) => !item.is_hidden);
    if (membership?.isPremium || membership?.isAdmin || !trial?.trialSeriesId) return byHidden;
    return byHidden.filter((item) => item.id === trial.trialSeriesId);
  }, [membership?.isAdmin, membership?.isPremium, seriesList, trial?.trialSeriesId]);

  const livePlayableSeriesBase = useMemo(
    () => visibleSeriesList.filter((item: any) => !isLiveDemoBlockedSeriesTitle(item?.title)),
    [visibleSeriesList],
  );
  const livePlayableSeries = useMemo(() => {
    if (!liveExcludeSeriesId) return livePlayableSeriesBase;
    const filtered = livePlayableSeriesBase.filter((item: any) => item?.id !== liveExcludeSeriesId);
    return filtered.length > 0 ? filtered : livePlayableSeriesBase;
  }, [liveExcludeSeriesId, livePlayableSeriesBase]);

  const visibleSeriesIds = useMemo(
    () => visibleSeriesList.map((item: any) => item.id).filter(Boolean).sort(),
    [visibleSeriesList],
  );

  const { data: playableVideoCount = 0, isLoading: isPlayableCountLoading } =
    usePlayableVideoCountBySeries(visibleSeriesIds);

  const filteredSeries = useMemo(() => {
    let result = [...visibleSeriesList];

    result = result.filter((series) => seriesMatchesTab(series, selectedTab));

    if (searchQuery.trim()) {
      const keyword = searchQuery.trim().toLowerCase();
      result = result.filter((series) => {
        const title = String(series.title || "").toLowerCase();
        const description = String(series.description || "").toLowerCase();
        return title.includes(keyword) || description.includes(keyword);
      });
    }

    if (selectedLevels.length > 0) {
      result = result.filter((series) => {
        const levels = Array.isArray(series.jlpt_levels) ? series.jlpt_levels : [];
        return levels.some((level) => selectedLevels.includes(level));
      });
    }

    result.sort((a: any, b: any) => {
      if (sortBy === "favoriteCount") {
        return Number(b.favorite_count || 0) - Number(a.favorite_count || 0);
      }
      return safeDate(b.updated_at).getTime() - safeDate(a.updated_at).getTime();
    });

    return result;
  }, [visibleSeriesList, selectedTab, searchQuery, selectedLevels, sortBy]);

  useEffect(() => {
    setListPage(1);
  }, [searchQuery, selectedTab, selectedLevels, sortBy]);

  useEffect(() => {
    if (!isLiveDemoMode) return;
    if (isLoading) return;
    if (livePlayableSeries.length === 0) return;

    const timers: number[] = [];
    const queue = (fn: () => void, delayMs: number) => {
      const t = window.setTimeout(fn, delayMs);
      timers.push(t);
    };

    const totalPages = Math.max(1, Math.ceil(filteredSeries.length / PAGE_SIZE) || 1);
    const browsePageLimit = Math.min(3, liveConfig.maxBrowsePage, totalPages);
    const firstPage = 1 + Math.floor(Math.random() * browsePageLimit);

    const shouldBrowseHomeFirst = Math.random() < liveConfig.homeBrowseChance;
    const delayMs = shouldBrowseHomeFirst
      ? liveConfig.homeBrowseMinMs +
        Math.floor(Math.random() * Math.max(500, liveConfig.homeBrowseMaxMs - liveConfig.homeBrowseMinMs))
      : liveConfig.homeQuickMinMs +
        Math.floor(Math.random() * Math.max(500, liveConfig.homeQuickMaxMs - liveConfig.homeQuickMinMs));

    const visitCount = shouldBrowseHomeFirst
      ? Math.max(1, 1 + Math.floor(Math.random() * browsePageLimit))
      : Math.max(1, 1 + (Math.random() < 0.35 && browsePageLimit > 1 ? 1 : 0));

    const pageSequence: number[] = [firstPage];
    while (pageSequence.length < visitCount) {
      const nextPage = 1 + Math.floor(Math.random() * browsePageLimit);
      const prev = pageSequence[pageSequence.length - 1];
      if (nextPage === prev && browsePageLimit > 1) continue;
      pageSequence.push(nextPage);
    }

    setListPage(pageSequence[0]);
    if (pageSequence.length > 1) {
      for (let i = 1; i < pageSequence.length; i += 1) {
        const at = Math.floor((delayMs * i) / (pageSequence.length + 1));
        const page = pageSequence[i];
        queue(() => {
          setListPage(page);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, at);
      }
    }

    if (Math.random() < liveConfig.homeScrollChance) {
      const actionMin = Math.min(liveConfig.homeScrollActionsMin, liveConfig.homeScrollActionsMax);
      const actionMax = Math.max(liveConfig.homeScrollActionsMin, liveConfig.homeScrollActionsMax);
      const actionCount = actionMin + Math.floor(Math.random() * (actionMax - actionMin + 1));
      for (let i = 0; i < actionCount; i += 1) {
        const at = Math.floor((delayMs * (i + 1)) / (actionCount + 1));
        queue(() => {
          const doc = document.documentElement;
          const maxScroll = Math.max(0, doc.scrollHeight - window.innerHeight);
          if (maxScroll <= 0) return;
          const currentTop = window.scrollY || window.pageYOffset || 0;
          const delta = Math.floor(window.innerHeight * (0.35 + Math.random() * 0.6));
          const direction = Math.random() < 0.5 ? -1 : 1;
          const nextTop = currentTop + direction * delta;
          const targetTop = Math.max(0, Math.min(maxScroll, nextTop));
          window.scrollTo({ top: targetTop, behavior: "smooth" });
        }, at);
      }
    }

    queue(() => {
      const targetPage = pageSequence[pageSequence.length - 1] || 1;
      const pageStart = (targetPage - 1) * PAGE_SIZE;
      const pageCandidates = filteredSeries
        .slice(pageStart, pageStart + PAGE_SIZE)
        .filter(
          (item: any) =>
            !isLiveDemoBlockedSeriesTitle(item?.title) &&
            (!liveExcludeSeriesId || item?.id !== liveExcludeSeriesId),
        );
      const pool = pageCandidates.length > 0 ? pageCandidates : livePlayableSeries;
      const target = pickWeightedSeries(pool);
      if (!target?.id) return;
      navigate(`/series/${target.id}?live=1`, { replace: true });
    }, delayMs);

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [filteredSeries, isLiveDemoMode, isLoading, liveConfig, liveExcludeSeriesId, livePlayableSeries, navigate]);

  const totalPages = Math.ceil(filteredSeries.length / PAGE_SIZE);
  const pagedSeries = filteredSeries.slice((listPage - 1) * PAGE_SIZE, listPage * PAGE_SIZE);

  const handleLogout = async () => {
    await signOut();
    toast({ title: "已退出登录" });
  };

  const handleNavigate = (page: string) => {
    if (page === "home") setCurrentPage("home");
    else if (page === "vocabulary") navigate("/vocabulary");
    else if (page === "favorites") navigate("/favorites");
    else if (page === "history") navigate("/history");
    else if (page === "feedback") navigate("/feedback");
    else if (page === "profile") navigate("/profile");
  };

  const toggleLevel = (level: string) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((item) => item !== level) : [...prev, level],
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((prev) => !prev)}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isLoggedIn={!!user}
        onLogout={handleLogout}
      />

      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur lg:hidden">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <MobileNav
              currentPage={currentPage}
              onNavigate={handleNavigate}
              isLoggedIn={!!user}
              onLogout={handleLogout}
            />
            <div className="flex items-center gap-2">
              <span className="text-2xl leading-none">🍡</span>
              <span className="font-bold text-foreground">dango</span>
            </div>
          </div>
        </div>
      </header>

      <main
        className={cn("transition-all duration-300", "lg:ml-16", !sidebarCollapsed && "lg:ml-60")}
      >
        <div className="mx-auto max-w-[1920px] px-4 py-6 lg:px-8 lg:py-8">
          {membership?.isAdmin ? (
            <div className="mb-4 flex justify-end gap-2">
              <Button
                variant={isLiveDemoMode ? "destructive" : "outline"}
                size="sm"
                onClick={() => navigate(isLiveDemoMode ? "/" : "/?live=1")}
              >
                {isLiveDemoMode ? "停止直播" : "直播演示"}
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" title="直播参数">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[360px] max-h-[78vh] overflow-y-auto space-y-3">
                  <div className="text-sm font-semibold">直播参数</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <Label className="self-center">主页浏览概率(%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(liveConfig.homeBrowseChance * 100)}
                      onChange={(e) => updateLiveConfig({ homeBrowseChance: Number(e.target.value || 0) / 100 })}
                    />

                    <Label className="self-center">主页慢停最短(秒)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={90}
                      value={Math.round(liveConfig.homeBrowseMinMs / 1000)}
                      onChange={(e) => updateLiveConfig({ homeBrowseMinMs: Number(e.target.value || 0) * 1000 })}
                    />

                    <Label className="self-center">主页慢停最长(秒)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={120}
                      value={Math.round(liveConfig.homeBrowseMaxMs / 1000)}
                      onChange={(e) => updateLiveConfig({ homeBrowseMaxMs: Number(e.target.value || 0) * 1000 })}
                    />

                    <Label className="self-center">切系列停顿(秒)</Label>
                    <Input
                      type="number"
                      min={2}
                      max={30}
                      value={Math.round(liveConfig.seriesPauseMs / 1000)}
                      onChange={(e) => updateLiveConfig({ seriesPauseMs: Number(e.target.value || 0) * 1000 })}
                    />

                    <Label className="self-center">回首页概率(%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(liveConfig.playerGoHomeChance * 100)}
                      onChange={(e) =>
                        updateLiveConfig({ playerGoHomeChance: Number(e.target.value || 0) / 100 })
                      }
                    />

                    <Label className="self-center">字幕滑动概率(%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(liveConfig.subtitleScrollChance * 100)}
                      onChange={(e) =>
                        updateLiveConfig({ subtitleScrollChance: Number(e.target.value || 0) / 100 })
                      }
                    />

                    <Label className="self-center">字幕滑动最少次数</Label>
                    <Input
                      type="number"
                      min={0}
                      max={8}
                      value={liveConfig.subtitleScrollActionsMin}
                      onChange={(e) =>
                        updateLiveConfig({ subtitleScrollActionsMin: Number(e.target.value || 0) })
                      }
                    />

                    <Label className="self-center">字幕滑动最多次数</Label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={liveConfig.subtitleScrollActionsMax}
                      onChange={(e) =>
                        updateLiveConfig({ subtitleScrollActionsMax: Number(e.target.value || 0) })
                      }
                    />

                    <Label className="self-center">主页滑动概率(%)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={Math.round(liveConfig.homeScrollChance * 100)}
                      onChange={(e) => updateLiveConfig({ homeScrollChance: Number(e.target.value || 0) / 100 })}
                    />

                    <Label className="self-center">主页滑动最少次数</Label>
                    <Input
                      type="number"
                      min={0}
                      max={8}
                      value={liveConfig.homeScrollActionsMin}
                      onChange={(e) =>
                        updateLiveConfig({ homeScrollActionsMin: Number(e.target.value || 0) })
                      }
                    />

                    <Label className="self-center">主页滑动最多次数</Label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={liveConfig.homeScrollActionsMax}
                      onChange={(e) =>
                        updateLiveConfig({ homeScrollActionsMax: Number(e.target.value || 0) })
                      }
                    />

                    <Label className="self-center">每视频AI次数</Label>
                    <Input
                      type="number"
                      min={0}
                      max={3}
                      value={liveConfig.aiPerVideoCount}
                      onChange={(e) => updateLiveConfig({ aiPerVideoCount: Number(e.target.value || 0) })}
                    />

                    <Label className="self-center">最多浏览页码</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={liveConfig.maxBrowsePage}
                      onChange={(e) => updateLiveConfig({ maxBrowsePage: Number(e.target.value || 1) })}
                    />
                  </div>
                  <div className="space-y-2 border-t border-border/60 pt-3">
                    <div className="text-xs font-medium text-muted-foreground">直播避雷视频</div>
                    <Label className="text-xs">屏蔽视频ID（逗号/换行分隔）</Label>
                    <Textarea
                      rows={3}
                      value={formatLiveConfigListInput(liveConfig.blockedVideoIds)}
                      onChange={(e) => updateLiveListConfig("blockedVideoIds", e.target.value)}
                      placeholder="例如：a1b2c3d4&#10;5f6e7d8c"
                      className="text-xs leading-5"
                    />
                    <Label className="text-xs">屏蔽标题关键词（逗号/换行分隔）</Label>
                    <Textarea
                      rows={3}
                      value={formatLiveConfigListInput(liveConfig.blockedVideoKeywords)}
                      onChange={(e) => updateLiveListConfig("blockedVideoKeywords", e.target.value)}
                      placeholder="例如：广告, 试听版, 片头"
                      className="text-xs leading-5"
                    />
                  </div>
                  <div className="flex justify-between pt-1">
                    <Button variant="ghost" size="sm" onClick={resetLiveConfig}>
                      恢复默认
                    </Button>
                    <span className="text-[11px] text-muted-foreground">修改后自动生效</span>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : null}
          <div className="mb-6">
            <AnnouncementBanner />
          </div>

          <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as PrimaryCategoryTab)} className="mb-6 w-full">
            <TabsList className="grid h-auto w-full grid-cols-4 bg-muted/60 p-1 sm:flex sm:w-auto">
              <TabsTrigger value="all" className="flex items-center gap-2 px-4 py-2">
                <LayoutGrid className="h-4 w-4" />
                全部
              </TabsTrigger>
              <TabsTrigger value="textbook" className="flex items-center gap-2 px-4 py-2">
                <Book className="h-4 w-4" />
                课本
              </TabsTrigger>
              <TabsTrigger value="news" className="flex items-center gap-2 px-4 py-2">
                <Newspaper className="h-4 w-4" />
                新闻
              </TabsTrigger>
              <TabsTrigger value="podcast" className="flex items-center gap-2 px-4 py-2">
                <Podcast className="h-4 w-4" />
                播客
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="mb-6 rounded-lg border border-border/50 bg-card p-4 shadow-sm">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
                <div className="w-full lg:w-1/3">
                  <SearchBar value={searchQuery} onChange={setSearchQuery} className="w-full" />
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="mr-1 text-sm font-medium text-muted-foreground">JLPT:</span>
                  {["N5", "N4", "N3", "N2", "N1"].map((level) => (
                    <div key={level} className="flex items-center space-x-2">
                      <Checkbox
                        id={`jlpt-${level}`}
                        checked={selectedLevels.includes(level)}
                        onCheckedChange={() => toggleLevel(level)}
                      />
                      <Label htmlFor={`jlpt-${level}`} className="cursor-pointer text-sm font-normal">
                        {level}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-1 flex flex-col items-center justify-between gap-4 border-t border-border/50 pt-4 sm:flex-row">
                <div className="order-2 flex w-full items-center gap-2 text-sm sm:order-1 sm:w-auto">
                  <span className="text-muted-foreground">可播放资源:</span>
                  <span className="text-base font-bold text-primary">
                    {isLoading || isPlayableCountLoading ? "..." : playableVideoCount}
                  </span>
                </div>

                <div className="order-1 flex w-full items-center gap-2 sm:order-2 sm:w-auto">
                  <span className="whitespace-nowrap text-sm text-muted-foreground">排序</span>
                  <Select value={sortBy} onValueChange={(value: "favoriteCount" | "updateTime") => setSortBy(value)}>
                    <SelectTrigger className="h-9 w-[140px] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="favoriteCount">收藏数量</SelectItem>
                      <SelectItem value="updateTime">更新时间</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-28">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <span className="text-muted-foreground">正在加载...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
              {pagedSeries.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-24 text-center">
                  <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                    <BookOpen className="h-10 w-10 text-muted-foreground/50" />
                  </div>
                  <h3 className="text-lg font-semibold">没有找到内容</h3>
                  <p className="mt-1 max-w-sm text-muted-foreground">试试调整搜索词或筛选条件</p>
                </div>
              ) : (
                pagedSeries.map((series) => (
                  <SeriesCard key={series.id} series={series} onClick={() => navigate(`/series/${series.id}`)} />
                ))
              )}
            </div>
          )}

          {totalPages > 1 ? (
            <div className="mt-8 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setListPage((prev) => Math.max(1, prev - 1))}
                      className={listPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        isActive={listPage === index + 1}
                        onClick={() => setListPage(index + 1)}
                        className="cursor-pointer"
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setListPage((prev) => Math.min(totalPages, prev + 1))}
                      className={listPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          ) : null}
        </div>
      </main>

      <footer
        className={cn(
          "mt-auto border-t border-border py-6 transition-all duration-300",
          "lg:ml-16",
          !sidebarCollapsed && "lg:ml-60",
        )}
      >
        <div className="space-y-1 px-4 text-center lg:px-6">
          <p className="text-sm text-muted-foreground">© 2026 dango</p>
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            桂ICP备2026001540号-2
          </a>
        </div>
      </footer>
    </div>
  );
}
